// Password reset page
import { useState, useEffect } from 'react';
import { Form, Link, useActionData, useLoaderData } from '@remix-run/react';
import { json, redirect } from '@remix-run/node';

// components
import UnderLineLink from '../components/Links/UnderLineLink';

// assets
import banner from '../images/homeHeroBanner.png';

export const meta = () => [
  { title: 'Reset Password - Colonial Flowers' },
  {
    name: 'description',
    content: 'Reset your password for Colonial Flowers Portal',
  },
];

export async function action({ request }) {
  try {
    const formData = await request.formData();
    const email = formData.get('email');
    const newPassword = formData.get('newPassword');
    const confirmPassword = formData.get('confirmPassword');
    const actionType = formData.get('_action');
    const errors = { inputErrors: {} };

    if (actionType === 'request') {
      // Step 1: Request password reset token

      if (!email) {
        errors.inputErrors.email = 'Please enter your email';
      }

      if (Object.keys(errors.inputErrors).length > 0) {
        return json({ errors }, { status: 400 });
      }

      // Import the API functions
      const { requestPasswordReset } = await import('../api.server');
      const requestResult = await requestPasswordReset(email);

      // Check for success - handle both lowercase and uppercase from XML response
      const isSuccess =
        requestResult.success === true ||
        requestResult.success === 'true' ||
        requestResult.Success === true ||
        requestResult.Success === 'true';

      if (isSuccess) {
        return json({
          success: true,
          resetMessage:
            'Password reset token sent to your email. Please check your inbox.',
        });
      }
      return json(
        {
          errors: {
            email: requestResult.error || 'Failed to get reset token',
          },
        },
        { status: 400 },
      );
    }

    if (actionType === 'reset') {
      // Step 2: Reset password

      if (!newPassword) {
        errors.inputErrors.newPassword = 'Please enter a new password';
      }

      if (!confirmPassword) {
        errors.inputErrors.confirmPassword = 'Please confirm your password';
      } else if (newPassword !== confirmPassword) {
        errors.inputErrors.confirmPassword = 'Passwords do not match';
      }
      const resetToken = formData.get('resetToken');

      if (!resetToken) {
        errors.inputErrors.resetToken = 'Reset token is required';
      }
      if (Object.keys(errors.inputErrors).length > 0) {
        return json({ errors }, { status: 400 });
      }

      // Import the API functions
		const { completePasswordReset } =
		  await import('../api.server');

		const resetResult =
		  await completePasswordReset(
			newPassword,
			resetToken,
		  );

      if (resetResult.success) {
        return redirect(
          '/?message=Password reset successfully. Please log in with your new password.',
        );
      }
      return json({ errors: { general: resetResult.error } }, { status: 400 });
    }
  } catch (error) {
    return json({ errors: { general: error.message } }, { status: 500 });
  }
}

export async function loader({ request }) {
  const url = new URL(request.url);

  const resetToken =
    url.searchParams.get('resetToken') ||
    url.searchParams.get('token');

  return json({ resetToken });
}

const showPasswordBtnIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    className="main-grid-item-icon"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const hidePasswordBtnIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    className="main-grid-item-icon"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" x2="23" y1="1" y2="23" />
  </svg>
);
export default function ResetPassword() {
  const actionData = useActionData();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inputErrors, setInputErrors] = useState({});
  const [email, setEmail] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (actionData?.success) {
      // Show success message for step 1
      setSuccess(actionData.resetMessage);
      setError('');
      setInputErrors({});
    } else if (actionData?.errors) {
      // Handle both string errors and object errors
      if (typeof actionData.errors === 'string') {
        setError(actionData.errors);
      } else if (actionData.errors?.inputErrors) {
        setInputErrors(actionData.errors.inputErrors);
        setError(''); // Clear any previous string errors
      } else if (actionData.errors?.general) {
        setError(actionData.errors.general);
      } else {
        setError('An error occurred. Please try again.');
      }
    }
  }, [actionData]);

  const loaderData = useLoaderData();

  // Handle email input change
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const renderStep1 = () => (
    <div className="w-full max-w-sm mx-auto lg:mx-0 lg:mt-32">
      <h1 className="text-4xl mb-8 uppercase">Request Password Reset</h1>
      {actionData?.resetMessage ? (
        <p className="text-green-600 mb-4">{actionData.resetMessage}</p>
      ) : (
        <Form method="post">
          <fieldset className="flex flex-col w-full">
            <p className="text-gray-600 mb-6">
              Enter your email address to receive a password reset token.
            </p>
			<label htmlFor="email" className="mb-1 font-light">
			  Email
			</label>

			<input
			  type="email"
			  name="email"
			  id="email"
			  value={email}
			  onChange={handleEmailChange}
			  className="border p-3 border-form-gray mb-4"
			  required
			/>

			{inputErrors?.email && (
			  <p className="text-red mb-4">{inputErrors.email}</p>
			)}
            <input type="hidden" name="_action" value="request" />

            <div className="flex gap-4 items-center mb-8">
              <button
                className="bg-green font-semibold uppercase text-black text-lg w-48 px-6 py-3 btn btn--green"
                type="submit"
              >
                Request Reset Token
              </button>
              <Link to="/" className="btn btn--gray">
                Back to Login
              </Link>
            </div>

            {error && (
              <p className="text-red mb-4">
                {typeof error === 'string'
                  ? error
                  : 'An error occurred. Please try again.'}
              </p>
            )}
            {success && <p className="text-green-600 mb-4">{success}</p>}
          </fieldset>
        </Form>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="w-full max-w-sm mx-auto lg:mx-0 lg:mt-32">
      <h1 className="text-4xl mb-8 uppercase">Reset Password</h1>
      <Form method="post">
        <fieldset className="flex flex-col w-full">
          <p className="text-gray-600 mb-6">
            Enter your new password to complete the reset.
          </p>

          <input
            type="hidden"
            name="resetToken"
            value={loaderData.resetToken}
          />

          <label htmlFor="newPassword" className="mb-1 font-light">
            New Password
          </label>
          <div className="flex items-center gap-2  mb-4 ">
            <input
              type={showNewPassword ? 'text' : 'password'}
              name="newPassword"
              id="newPassword"
              className="border p-3 border-form-gray w-full"
              required
            />
            <button
              type="button"
              className="b-0 bg-transparent cursor-pointer p-2"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? hidePasswordBtnIcon : showPasswordBtnIcon}
            </button>
          </div>
          {inputErrors?.newPassword && (
            <p className="text-red mb-4">{inputErrors.newPassword}</p>
          )}

          <label htmlFor="confirmPassword" className="mb-1 font-light">
            Confirm New Password
          </label>
          <div className="flex items-center gap-2  mb-4 ">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              id="confirmPassword"
              className="border p-3 border-form-gray w-full"
              required
            />
            <button
              type="button"
              className="b-0 bg-transparent cursor-pointer p-2"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? hidePasswordBtnIcon : showPasswordBtnIcon}
            </button>
          </div>
          {inputErrors?.confirmPassword && (
            <p className="text-red mb-4">{inputErrors.confirmPassword}</p>
          )}
          <input type="hidden" name="_action" value="reset" />

          <div className="flex gap-4 items-center mb-8">
            <button
              className="bg-green font-semibold uppercase text-black text-lg w-48 px-6 py-3 btn btn--green"
              type="submit"
            >
              Reset Password
            </button>
          </div>

          {error && (
            <p className="text-red mb-4">
              {typeof error === 'string'
                ? error
                : 'An error occurred. Please try again.'}
            </p>
          )}
          {success && <p className="text-green-600 mb-4">{success}</p>}
        </fieldset>
      </Form>
    </div>
  );

  return (
    <main className="flex flex-col lg:flex-row h-screen">
      <section
        id="heroImage"
        className="w-full lg:w-1/2 h-1/4 sm:h-2/5 lg:h-full"
      >
        <img
          src={banner}
          alt="purple flowers in hanging baskets"
          className="h-full w-full object-cover"
        />
      </section>
      <section
        id="reset-password"
        className="w-full lg:w-1/2 h-1/2 lg:h-full flex flex-col items-center lg:items-start justify-center px-4 lg:px-24 my-auto lg:my-0"
      >
        {loaderData.resetToken ? renderStep2() : renderStep1()}
        <div className="extra-links flex flex-col mx-auto lg:mx-0">
          <div className="flex gap-5 mb-8">
            <p className="font-light">Remember your password?</p>
            <UnderLineLink to="/">Back to Login</UnderLineLink>
          </div>
          <div className="flex gap-3">
			<a
			  href="https://colonialfloristsltd.com/privacy-policy"
			  className="font-light"
			  target="_blank"
			  rel="noopener noreferrer"
			>
			  Privacy Policy
			</a>

			<span className="font-light">|</span>

			<a
			  href="https://colonialfloristsltd.com/privacy-policy"
			  className="font-light"
			  target="_blank"
			  rel="noopener noreferrer"
			>
			  Terms &amp; Conditions
			</a>
          </div>
        </div>
      </section>
    </main>
  );
}
